/**
 * Roadmap #17 — tap bays and parking spots on the shop floor to say what a
 * camera can see. Read-only geometry (nothing here moves a bay), the same
 * pan / pinch / zoom camera as the Guided Start picker, and every target is a
 * real button: tap or click, never hover. Selected targets are filled.
 *
 * Coverage is capability, not occupancy — nothing here knows or shows who is
 * parked where.
 */
import { useEffect, useMemo, useRef, type MouseEvent, type TouchEvent } from 'react';
import { Maximize2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useMapCamera, type MapRect } from '@/hooks/useMapCamera';
import type { CameraTarget } from '@/lib/cameras/api';

const isMapControl = (t: HTMLElement) => !!t.closest('[data-map-control]');

export interface CoverageMapProps {
  bays: readonly CameraTarget[];
  spots: readonly CameraTarget[];
  selectedBayIds: ReadonlySet<string>;
  selectedSpotIds: ReadonlySet<string>;
  onToggleBay?: (id: string) => void;
  onToggleSpot?: (id: string) => void;
  className?: string;
}

export function CoverageMap({
  bays, spots, selectedBayIds, selectedSpotIds, onToggleBay, onToggleSpot, className,
}: CoverageMapProps) {
  const drawnBays = useMemo(() => bays.filter((b) => b.rect), [bays]);
  const drawnSpots = useMemo(() => spots.filter((s) => s.rect), [spots]);
  const items = useMemo<MapRect[]>(
    () => [...drawnBays, ...drawnSpots].map((t) => ({ x: t.rect!.x, y: t.rect!.y, w: t.rect!.w, h: t.rect!.h })),
    [drawnBays, drawnSpots],
  );
  const camera = useMapCamera({ items, ignorePanStart: isMapControl });
  const { containerRef, zoom, offset, bounds, isDragging, fitToScreen } = camera;

  const touched = useRef(false);
  const fitRef = useRef(() => {});
  fitRef.current = () => fitToScreen();
  useEffect(() => {
    touched.current = false;
    const raf = requestAnimationFrame(() => fitRef.current());
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return () => cancelAnimationFrame(raf);
    const ro = new ResizeObserver(() => { if (!touched.current) fitRef.current(); });
    ro.observe(el);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [items, containerRef]);

  const onMouseDown = (e: MouseEvent) => { touched.current = true; camera.handlers.onMouseDown(e); };
  const onTouchStart = (e: TouchEvent<HTMLDivElement>) => { touched.current = true; camera.handlers.onTouchStart(e); };
  // A pan that ended on a target is not a selection.
  const tap = (fn: ((id: string) => void) | undefined, id: string) => {
    if (camera.consumeDrag() || !fn) return;
    fn(id);
  };

  if (items.length === 0) return null;

  const target = (t: CameraTarget, kind: 'bay' | 'spot') => {
    const selected = kind === 'bay' ? selectedBayIds.has(t.id) : selectedSpotIds.has(t.id);
    const toggle = kind === 'bay' ? onToggleBay : onToggleSpot;
    return (
      <button
        key={`${kind}-${t.id}`}
        type="button"
        aria-pressed={selected}
        aria-label={`${t.label} — ${selected ? 'covered' : 'not covered'}`}
        data-testid={`coverage-map-${kind}-${t.id}`}
        data-selected={selected || undefined}
        disabled={!toggle}
        onClick={() => tap(toggle, t.id)}
        className={cn(
          'absolute flex items-center justify-center overflow-hidden rounded-md border-2 p-0.5 text-center leading-tight',
          kind === 'bay' ? 'text-sm font-semibold' : 'border-dashed text-xs',
          selected
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-muted-foreground/40 bg-background/80 text-foreground',
        )}
        style={{ left: t.rect!.x - bounds.minX, top: t.rect!.y - bounds.minY, width: t.rect!.w, height: t.rect!.h }}
      >
        <span className="break-words">{t.label}</span>
      </button>
    );
  };

  return (
    <div
      ref={containerRef}
      data-testid="coverage-map"
      role="group"
      aria-label="Shop floor — tap a bay or spot to add or remove it"
      className={cn('relative w-full overflow-hidden rounded-md border bg-muted/20 select-none h-[clamp(220px,45dvh,480px)]', className)}
      style={{ cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none' }}
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
      onTouchMove={camera.handlers.onTouchMove}
      onTouchEnd={camera.handlers.onTouchEnd}
    >
      <div
        className="relative will-change-transform"
        style={{
          transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
          transformOrigin: '0 0',
          width: `${bounds.width}px`,
          height: `${bounds.height}px`,
        }}
      >
        {drawnSpots.map((s) => target(s, 'spot'))}
        {drawnBays.map((b) => target(b, 'bay'))}
      </div>
      <button
        type="button"
        data-map-control
        aria-label="Fit the whole shop floor"
        onClick={() => fitRef.current()}
        className="absolute bottom-2 right-2 flex h-11 w-11 items-center justify-center rounded-md border bg-background/90 shadow-sm"
      >
        <Maximize2 className="h-4 w-4" />
      </button>
    </div>
  );
}
